import { useMemo } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  AcademicYear,
  Course,
  Term,
  TermCourse,
  Student,
  StudentRosterStatus,
  Enrollment,
  Teacher,
  Department,
  Parent,
  ParentStudentLink,
  ParentRelationship,
} from '@/types/api'

// Query keys
export const adminKeys = {
  all: ['admin'] as const,
  // Academic Years
  academicYears: () => [...adminKeys.all, 'academicYears'] as const,
  // Shared
  courses: () => [...adminKeys.all, 'courses'] as const,
  terms: () => [...adminKeys.all, 'terms'] as const,
  termCourses: () => [...adminKeys.all, 'termCourses'] as const,
  students: () => [...adminKeys.all, 'students'] as const,
  enrollments: () => [...adminKeys.all, 'enrollments'] as const,
  // Teachers
  teachers: () => [...adminKeys.all, 'teachers'] as const,
  departments: () => [...adminKeys.all, 'departments'] as const,
  // Parents
  parents: () => [...adminKeys.all, 'parents'] as const,
  parentStudentLinks: () => [...adminKeys.all, 'parentStudentLinks'] as const,
}

// ============ Academic Years Queries ============

export function useAcademicYears() {
  return useQuery({
    queryKey: adminKeys.academicYears(),
    queryFn: async () => {
      const res = await api.get<{ academicYears: AcademicYear[] }>('/api/academic-years')
      return res.data.academicYears
    },
  })
}

export function useActiveAcademicYear() {
  return useQuery({
    queryKey: [...adminKeys.academicYears(), 'active'],
    queryFn: async () => {
      const res = await api.get<{ academicYear: AcademicYear }>('/api/academic-years/active')
      return res.data.academicYear
    },
  })
}

export function useCreateAcademicYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      name: string
      startDate: string
      endDate: string
      isActive?: boolean
    }) => {
      const res = await api.post<{ academicYear: AcademicYear }>('/api/academic-years', data)
      return res.data.academicYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.academicYears() })
    },
  })
}

export function useUpdateAcademicYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        name: string
        startDate: string
        endDate: string
      }>
    }) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}`, data)
      return res.data.academicYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.academicYears() })
    },
  })
}

export function useActivateAcademicYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}/activate`)
      return res.data.academicYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.academicYears() })
    },
  })
}

export function useArchiveAcademicYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}/archive`)
      return res.data.academicYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.academicYears() })
    },
  })
}

export function useDeleteAcademicYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/academic-years/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.academicYears() })
    },
  })
}

// ============ Shared Queries ============

export function useCourses() {
  return useQuery({
    queryKey: adminKeys.courses(),
    queryFn: async () => {
      const res = await api.get<{ courses: Course[] }>('/api/courses')
      return res.data.courses
    },
  })
}

export function useTerms(
  academicYearId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: academicYearId ? [...adminKeys.terms(), academicYearId] : adminKeys.terms(),
    queryFn: async () => {
      const url = academicYearId ? `/api/terms?academicYearId=${academicYearId}` : '/api/terms'
      const res = await api.get<{ terms: Term[] }>(url)
      return res.data.terms
    },
    // Callers that only want a specific year's terms can sit the query out
    // rather than falling through to the unfiltered `/api/terms` fetch.
    enabled: options?.enabled ?? true,
  })
}

export function useTermCourses() {
  return useQuery({
    queryKey: adminKeys.termCourses(),
    queryFn: async () => {
      const res = await api.get<{ termCourses: TermCourse[] }>('/api/term-courses')
      return res.data.termCourses
    },
  })
}

/** Matches DEFAULT_PAGE_SIZE on the API; the roster is served a page at a time. */
export const STUDENTS_PAGE_SIZE = 50

/** Matches MAX_PAGE_SIZE on the API - the fewest round trips a full roster takes. */
const STUDENTS_MAX_PAGE_SIZE = 100

interface StudentsPage {
  students: Student[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

async function fetchStudentsPage(
  page: number,
  limit: number,
  status: StudentRosterStatus = 'active'
): Promise<StudentsPage> {
  // Left off entirely for the default so the roster request is byte-for-byte the
  // one it has always been; `status` is only ever sent when asking for the slice
  // the endpoint does not serve by default.
  const query = status === 'active' ? '' : `&status=${status}`
  const res = await api.get<StudentsPage>(`/api/students?page=${page}&limit=${limit}${query}`)
  return res.data
}

/**
 * Every page of the roster, concatenated.
 *
 * For the call sites that filter over the whole roster client-side - "students
 * not yet linked to this parent", "students eligible for this course" - where a
 * single page would silently narrow the choices rather than visibly truncate a
 * list. Prefer `useStudentsInfinite` for anything the user reads a page at a
 * time; this one is a full fetch.
 */
export function useAllStudents() {
  return useQuery({
    // Under adminKeys.students() so the student mutations invalidate it too.
    queryKey: [...adminKeys.students(), 'all'] as const,
    queryFn: async () => {
      const first = await fetchStudentsPage(1, STUDENTS_MAX_PAGE_SIZE)

      // No pagination block means an unpaged payload - what came back is all of it.
      const totalPages = first.pagination?.totalPages ?? 1
      if (totalPages <= 1) return first.students

      // The roster has a total order server-side, so the offsets line up and the
      // remaining pages can be fetched at once rather than one after another.
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          fetchStudentsPage(i + 2, STUDENTS_MAX_PAGE_SIZE)
        )
      )

      return [first, ...rest].flatMap((page) => page.students)
    },
  })
}

/**
 * The roster a page at a time, for a list the user pages through. Use
 * `useAllStudents` where the whole roster has to be in hand at once.
 *
 * `status` picks the slice: 'active' is the roster the endpoint serves by
 * default, 'withdrawn' the students it derives out of it. The two are separate
 * cache entries, so opening the withdrawn view never disturbs the loaded pages
 * of the roster, and a withdrawal invalidates both.
 */
export function useStudentsInfinite(
  limit: number = STUDENTS_PAGE_SIZE,
  status: StudentRosterStatus = 'active'
) {
  // Keyed under adminKeys.students() so the student mutations, which invalidate
  // that prefix, refetch the loaded pages here too.
  const query = useInfiniteQuery({
    queryKey: [...adminKeys.students(), 'infinite', limit, status] as const,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchStudentsPage(pageParam, limit, status),
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage.pagination
      // No pagination block means a single unpaged payload - nothing to follow.
      if (!pagination) return undefined
      return pagination.page < pagination.totalPages ? allPages.length + 1 : undefined
    },
  })

  const students = useMemo(
    () => query.data?.pages.flatMap((p) => p.students) ?? [],
    [query.data]
  )

  return {
    ...query,
    students,
    // The server's count of the whole roster, not just what has been loaded.
    total: query.data?.pages[0]?.pagination?.total ?? students.length,
  }
}

export function useEnrollments() {
  return useQuery({
    queryKey: adminKeys.enrollments(),
    queryFn: async () => {
      const res = await api.get<{ enrollments: Enrollment[] }>('/api/enrollments')
      return res.data.enrollments
    },
  })
}

// ============ Teachers Queries ============

export function useTeachers() {
  return useQuery({
    queryKey: adminKeys.teachers(),
    queryFn: async () => {
      const res = await api.get<{ teachers: Teacher[] }>('/api/teachers')
      return res.data.teachers
    },
  })
}

export function useDepartments() {
  return useQuery({
    queryKey: adminKeys.departments(),
    queryFn: async () => {
      const res = await api.get<{ departments: Department[] }>('/api/departments')
      return res.data.departments
    },
  })
}

export function useCreateTeacher() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      firstName: string
      lastName: string
      // Optional: a staff record does not have to be an account.
      email?: string | null
      password?: string
      employeeId: string
      departmentId?: string
    }) => {
      const res = await api.post<{ teacher: Teacher }>('/api/teachers', data)
      return res.data.teacher
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.teachers() })
    },
  })
}

export function useUpdateTeacher() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        firstName: string
        lastName: string
        email: string | null
        employeeId: string
        departmentId: string | null
      }>
    }) => {
      const res = await api.put<{ teacher: Teacher }>(`/api/teachers/${id}`, data)
      return res.data.teacher
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.teachers() })
    },
  })
}

export function useDeleteTeacher() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/teachers/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.teachers() })
    },
  })
}

// ============ Departments Mutations ============

export function useCreateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await api.post<{ department: Department }>('/api/departments', data)
      return res.data.department
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.departments() })
    },
  })
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { name?: string; description?: string | null }
    }) => {
      const res = await api.put<{ department: Department }>(`/api/departments/${id}`, data)
      return res.data.department
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.departments() })
    },
  })
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/departments/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.departments() })
      // Teachers/courses may reference departments, so refresh those too.
      queryClient.invalidateQueries({ queryKey: adminKeys.teachers() })
    },
  })
}

// ============ Parents Queries ============

export function useParents() {
  return useQuery({
    queryKey: adminKeys.parents(),
    queryFn: async () => {
      const res = await api.get<{ parents: Parent[] }>('/api/parents')
      return res.data.parents
    },
  })
}

export function useParentStudentLinks() {
  return useQuery({
    queryKey: adminKeys.parentStudentLinks(),
    queryFn: async () => {
      const res = await api.get<{ links: ParentStudentLink[] }>('/api/parent-students')
      return res.data.links
    },
  })
}

export function useCreateParent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      firstName: string
      lastName: string
      // Optional: the directory lists parents who never sign in.
      email?: string | null
      password?: string
    }) => {
      const res = await api.post<{ parent: Parent }>('/api/parents', data)
      return res.data.parent
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.parents() })
    },
  })
}

export function useUpdateParent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { firstName: string; lastName: string; email: string | null }
    }) => {
      const res = await api.put<{ parent: Parent }>(`/api/parents/${id}`, data)
      return res.data.parent
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.parents() })
    },
  })
}

export function useDeleteParent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/parents/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.parents() })
    },
  })
}

export function useLinkParentStudent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      parentId: string
      studentId: string
      relationship: ParentRelationship
      isPrimary: boolean
    }) => {
      const res = await api.post('/api/parent-students', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.parentStudentLinks() })
    },
  })
}

export function useUnlinkParentStudent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (linkId: string) => {
      await api.delete(`/api/parent-students/${linkId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.parentStudentLinks() })
    },
  })
}

// ============ Courses Page Mutations ============

export function useCreateCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      courseName: string
      courseCode: string
      level: 'HL' | 'SL' | 'Standard'
      credits: number
      departmentId: string | null
      description?: string
      // Some subjects mix grade levels (e.g. Theatre combines 7 and 8); the deprecated
      // `gradeLevel` scalar is still accepted by the API for backward compatibility but
      // the frontend now always writes the array.
      gradeLevels?: number[]
    }) => {
      const res = await api.post<{ course: Course }>('/api/courses', data)
      return res.data.course
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.courses() })
    },
  })
}

export function useUpdateCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        courseName: string
        courseCode: string
        level: 'HL' | 'SL' | 'Standard'
        credits: number
        departmentId: string | null
        description?: string
        gradeLevels?: number[]
      }
    }) => {
      const res = await api.put<{ course: Course }>(`/api/courses/${id}`, data)
      return res.data.course
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.courses() })
    },
  })
}

export function useDeleteCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/courses/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.courses() })
    },
  })
}

export function useCreateTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      termName: string
      academicYear: string
      academicYearId?: string
      startDate?: string
      endDate?: string
      gradeSubmissionDeadline?: string
    }) => {
      const res = await api.post<{ term: Term }>('/api/terms', data)
      return res.data.term
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() })
    },
  })
}

export function useUpdateTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        termName: string
        academicYear: string
        academicYearId?: string
        startDate?: string
        endDate?: string
        gradeSubmissionDeadline?: string
      }
    }) => {
      const res = await api.put<{ term: Term }>(`/api/terms/${id}`, data)
      return res.data.term
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() })
    },
  })
}

export function useDeleteTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/terms/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() })
    },
  })
}

export function useLockTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/terms/${id}/lock`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() })
    },
  })
}

export function useUnlockTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/terms/${id}/unlock`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.terms() })
    },
  })
}

export function useCreateTermCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { termId: string; courseId: string; teacherId?: string }) => {
      const res = await api.post<{ termCourse: TermCourse }>('/api/term-courses', data)
      return res.data.termCourse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.termCourses() })
    },
  })
}

export function useDeleteTermCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/term-courses/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.termCourses() })
      queryClient.invalidateQueries({ queryKey: adminKeys.enrollments() })
    },
  })
}

export function useSaveEnrollments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      termCourseId,
      currentEnrollments,
      selectedStudentIds,
    }: {
      termCourseId: string
      currentEnrollments: Enrollment[]
      selectedStudentIds: Set<string>
    }) => {
      // Only consider active enrollments as "current" - withdrawn/dropped students can be re-enrolled
      const activeEnrollments = currentEnrollments.filter((e) => e.status === 'active')
      const currentStudentIds = new Set(activeEnrollments.map((e) => e.studentId))

      const toEnroll = Array.from(selectedStudentIds).filter(
        (id) => !currentStudentIds.has(id)
      )
      const toUnenroll = activeEnrollments.filter(
        (e) => !selectedStudentIds.has(e.studentId)
      )

      await Promise.all([
        ...toEnroll.map((studentId) =>
          api.post('/api/enrollments', {
            studentId,
            termCourseId,
            status: 'active',
            enrollmentDate: new Date().toISOString(),
          })
        ),
        // Withdraw removed enrollments (soft delete - preserves grades)
        ...toUnenroll.map((e) => api.delete(`/api/enrollments/${e.id}`)),
      ])

      return { enrolled: toEnroll.length, unenrolled: toUnenroll.length }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.enrollments() })
    },
  })
}

// ============ Student Mutations ============

export function useCreateStudent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      studentId: string
      firstName: string
      lastName: string
      email?: string | null
      password?: string
      gradeLevel: number
      graduationYear: number
      programType: 'IHP' | 'DP' | 'Both'
    }) => {
      const res = await api.post<{ student: Student }>('/api/students', data)
      return res.data.student
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.students() })
    },
  })
}

export function useUpdateStudent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        firstName: string
        lastName: string
        email: string | null
        studentId: string
        gradeLevel: number
        graduationYear: number
        programType: 'IHP' | 'DP' | 'Both'
        cumulativeGpa: number | null
        credits: number | null
      }>
    }) => {
      const res = await api.put<{ student: Student }>(`/api/students/${id}`, data)
      return res.data.student
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.students() })
    },
  })
}

export function useDeleteStudent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/students/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.students() })
    },
  })
}
